import Sentry from '@/services/sentry/Sentry';
import {
  CampaignStatsCreator,
  CampaignStatsCreatorSchema,
} from '@/types/campaignStatsCreator';
import {PaginationMeta, PaginationMetaSchema} from '@/types/socialMediaPost';
import api from '../index';

export interface CampaignStatsCreatorsParams {
  campaignId: string;
  page?: number;
  perPage?: number;
  sort?: string;
  q?: string;
}

export interface CampaignStatsCreatorsResponse {
  creators: CampaignStatsCreator[];
  pagination: PaginationMeta;
}

function transformCreator(raw: unknown): CampaignStatsCreator | null {
  const result = CampaignStatsCreatorSchema.safeParse(raw);

  if (!result.success) {
    Sentry.addBreadcrumb({
      message: `Campaign stats creator validation failed: ${result.error.message}`,
      level: 'error',
      type: 'error',
    });
    return null;
  }

  return result.data;
}

async function fetchCampaignStatsCreators(
  params: CampaignStatsCreatorsParams
): Promise<CampaignStatsCreatorsResponse> {
  const {campaignId, page, perPage, sort, q} = params;

  if (!campaignId) {
    throw new Error('campaignId is required');
  }

  const response = await api.get(`/campaigns/${campaignId}/stats_creators`, {
    params: {
      ...(page !== undefined && {page}),
      ...(perPage !== undefined && {per_page: perPage}),
      ...(sort && {sort}),
      ...(q && {q}),
    },
  });

  const creators: CampaignStatsCreator[] = (response.data || [])
    .map(transformCreator)
    .filter(
      (creator: CampaignStatsCreator | null): creator is CampaignStatsCreator =>
        creator !== null
    );

  const parsePagination = PaginationMetaSchema.safeParse(
    response.meta?.pagination
  );

  let pagination: PaginationMeta;
  if (parsePagination.success) {
    pagination = parsePagination.data;
  } else {
    pagination = {
      currentPage: page ?? 1,
      nextPage: null,
      prevPage: null,
      totalEntries: creators.length,
      totalPages: creators.length > 0 ? 1 : 0,
    };
  }

  return {creators, pagination};
}

const campaignStatsCreatorsFetchers = {
  fetchCreators: fetchCampaignStatsCreators,
};

export default campaignStatsCreatorsFetchers;
